import { arrayMove } from '@dnd-kit/sortable'
import type { UpdateResolver } from '@urql/exchange-graphcache'
import { graphql } from '@/generated/gql'
import {
    type ActionCreatedSubscriptionVariables,
    type ActionDeletedOnMeetingIdSubscription,
    type ActionDeletedSubscriptionVariables,
    type ActionIntegrationLinkedSubscription,
    ActionQueryCategory,
    type ActionUpdatedByMeetingIdSubscription,
    type ActionUpdatedSubscriptionVariables,
    type CreateSubActionMutation,
    type ReorderSubActionsMutationVariables,
    SortEnumType,
    type StarActionMutation,
    type UnLinkActionFromMutation,
    type UnstarActionMutation,
} from '@/generated/gql/graphql'
import { invalidateCache } from '../utils/invalidateCache'

const subActionsFragment = graphql(`
    fragment SubActionsFragment on Action {
        id
        subActions {
            id
            title
            dueDate
            order
            creatorUserId
            completeStatus {
                status
                isCompleted
            }
            meeting {
                id
                title
                permissions
            }
            assignees{
                assigneeRole
                user {
                    id
                    fullName
                    firstName
                    lastName
                    email
                    imageUrl
                    role
                    isConfirmedEmail
                    meetplaceId
                    isConfirmedEmail
                    isMeetplaceInvitationInvalid
                    isMeetplaceInvitationExpired
                    jobTitle
                    userStatus
                }
            }
            delegatees{
                assigneeRole
                user {
                    id
                    fullName
                    firstName
                    lastName
                    email
                    imageUrl
                    role
                    isConfirmedEmail
                    meetplaceId
                    isConfirmedEmail
                    isMeetplaceInvitationInvalid
                    isMeetplaceInvitationExpired
                    jobTitle
                    userStatus
                }
            }
        }
    }
`)

export const actionsMutationsUpdaters: Record<string, UpdateResolver> = {
    removeJiraLinkage: (result, args, cache) => {
        invalidateCache(cache, 'action')
    },
    linkActionToJira: (result, args, cache) => {
        invalidateCache(cache, 'action')
    },
    updateActionCompleteStatus: (result, args, cache) => {
        invalidateCache(cache, ['actions', 'actionsV1', 'actionsClusteredV1'])
    },
    createAction: (result, args, cache) => {
        invalidateCache(cache, ['actions', 'actionsV1'])
    },
    createActionV1: (result: CreateSubActionMutation, args, cache) => {
        invalidateCache(cache, ['actionsV1', 'actionsClusteredV1'])

        const isSubAction = result.createActionV1.action.parentId != null
        if (isSubAction) {
            const cachedParentAction = cache.readFragment(subActionsFragment, {
                __typename: 'Action',
                id: result.createActionV1.action.parentId,
            })
            if (cachedParentAction) {
                cache.writeFragment(subActionsFragment, {
                    id: result.createActionV1.action.parentId,
                    subActions: cachedParentAction.subActions.concat(
                        result.createActionV1.action,
                    ),
                })
            }
        }
    },
    updateActionDueDate: (result, args, cache) => {
        invalidateCache(cache, [
            'actions',
            'actionsV1',
            'actionsClusteredV1',
            'starredItems',
            'starredItemsV1',
        ])
    },
    editAction: (result, args, cache) => {
        invalidateCache(cache, [
            'actions',
            'actionsV1',
            'actionEligibleWatchers',
            'actionsClusteredV1',
            'starredItems',
            'starredItemsV1',
        ])
    },
    reorderSubaction: (
        _result,
        args: ReorderSubActionsMutationVariables,
        cache,
    ) => {
        // Invalidate the subactions query for this parent action
        cache.invalidate('Query', 'actionsV1', {
            actionQueryCategory: ActionQueryCategory.AllActions,
            first: 20,
            order: [{ order: SortEnumType.Asc }],
            where: {
                isArchived: {
                    eq: false,
                },
                parentId: {
                    eq: args.input.parentId,
                },
            },
        })
        const cachedParentAction = cache.readFragment(subActionsFragment, {
            __typename: 'Action',
            id: args.input.parentId,
        })
        if (cachedParentAction != null) {
            cache.writeFragment(subActionsFragment, {
                id: args.input.parentId,
                subActions: arrayMove(
                    cachedParentAction.subActions,
                    args.input.subactionOldIndex,
                    args.input.subactionNewIndex,
                ),
            })
        }
    },
    deleteAction: (_parent, _args, cache) => {
        invalidateCache(cache, [
            'actionsClusteredV1',
            'actionsV1',
            'starredItems',
            'starredItemsV1',
        ])
    },
    unStarActionV1: (parent: UnstarActionMutation, _args, cache) => {
        invalidateCache(cache, [
            'actionsClusteredV1',
            'starredItems',
            'starredItemsV1',
        ])
        cache.invalidate({
            __typename: 'Action',
            id: parent.unStarActionV1.action.id,
        })
    },
    starActionV1: (parent: StarActionMutation, _args, cache) => {
        invalidateCache(cache, [
            'actionsClusteredV1',
            'starredItems',
            'starredItemsV1',
            'actionsV1',
        ])
        cache.invalidate({
            __typename: 'Action',
            id: parent.starActionV1.action.id,
        })
    },
    unLinkActionFrom: (parent: UnLinkActionFromMutation, args, cache) => {
        cache.invalidate({
            __typename: 'Action',
            id: parent.unLinkActionFrom.action.id,
        })
        invalidateCache(cache, ['actionsClusteredV1'])
    },
}

export const actionsSubscriptionsUpdaters: Record<string, UpdateResolver> = {
    actionUpdatedByMeetingId: (
        parent: ActionUpdatedByMeetingIdSubscription,
        _args,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Action',
            id: parent.actionUpdatedByMeetingId.action.id,
        })
    },
    actionDeletedOnMeetingV1: (
        parent: ActionDeletedOnMeetingIdSubscription,
        _args,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Action',
            id: parent.actionDeletedOnMeetingV1.action.id,
        })
    },
    actionCreatedV1: (
        _parent,
        args: ActionCreatedSubscriptionVariables,
        cache,
    ) => {
        cache.invalidate('Query', 'actionsV1', {
            where: {
                isArchived: {
                    eq: false,
                },
                meetingId: {
                    eq: args.meetingId,
                },
            },
        })
    },
    actionUpdated: (
        _result,
        args: ActionUpdatedSubscriptionVariables,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Action',
            id: args.actionId,
        })
        invalidateCache(cache, 'actionEligibleWatchers')
    },
    actionDeleted: (
        _result,
        args: ActionDeletedSubscriptionVariables,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Action',
            id: args.actionId,
        })
    },
    actionIntegrationLinked: (
        result: ActionIntegrationLinkedSubscription,
        _args,
        cache,
    ) => {
        const { key, lastSynced, linked, needsResync, url } =
            result.actionIntegrationLinked.action.integrations
        cache.writeFragment(
            graphql(`
                fragment IntegrationFragment on Action {
                    id
                    jiraLinkage {
                        __typename
                        key
                        lastSynced
                        linked
                        needsResync
                        url
                    }
                }
            `),
            {
                id: result.actionIntegrationLinked.action.id,
                jiraLinkage: {
                    __typename: 'JiraLinkage',
                    key,
                    lastSynced,
                    linked,
                    needsResync,
                    url,
                },
            },
        )
    },
}
